
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingData } from '@/types/onboarding';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';
import { calculateAge } from '@/utils/onboarding-utils';
import { datadogRum } from '@datadog/browser-rum';

export const useOnboardingOperations = (
  data: OnboardingData,
  setLoading: (loading: boolean) => void,
  lastSavedStep: number
) => {
  const navigate = useNavigate();
  const { user, refreshProfileData } = useAuth();
  const { refetch: refetchProfile } = useProfile();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Flag para controlar se devemos sincronizar automaticamente
  const isManualNavigation = useRef(false);
  const hasInitialized = useRef(false);

  // Ref para armazenar função de cleanup de upload de fotos
  const photoUploadCleanupRef = useRef<(() => void) | null>(null);

  const totalSteps = 7; // REDUZIDO de 9 para 7

  // Function to register photo upload cleanup
  const registerPhotoUploadCleanup = useCallback((cleanupFn: () => void) => {
    photoUploadCleanupRef.current = cleanupFn;
    debugLogger.debug('OnboardingOperations: Photo upload cleanup function registered');
  }, []);

  // Synchronize currentStep with lastSavedStep apenas na inicialização
  useEffect(() => {
    if (lastSavedStep > 0 && !hasInitialized.current) {
      // Start from the last saved step (where user actually was)
      const startStep = Math.min(lastSavedStep, 7);
      setCurrentStep(startStep);
      hasInitialized.current = true;
      debugLogger.debug('useOnboardingOperations: Initial sync with saved progress:', {
        lastSavedStep,
        startStep
      });
    }
  }, [lastSavedStep]);

  const saveCurrentStep = useCallback(async (stepNumber: number) => {
    if (!user) return;

    try {
      debugLogger.debug('useOnboardingOperations: Saving current step:', stepNumber);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          last_onboarding_step: stepNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        debugLogger.error('useOnboardingOperations: Error saving step:', error);
      } else {
        debugLogger.debug('useOnboardingOperations: Step saved successfully:', stepNumber);
      }
    } catch (error) {
      debugLogger.error('useOnboardingOperations: Exception saving step:', error);
    }
  }, [user]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= totalSteps) {
      isManualNavigation.current = true;
      setCurrentStep(step);
      saveCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      debugLogger.debug(`OnboardingOperations: Manual navigation to step ${step}`);
    }
  }, [totalSteps, saveCurrentStep]);

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      const nextStepNumber = currentStep + 1;
      isManualNavigation.current = true;
      setCurrentStep(nextStepNumber);
      saveCurrentStep(nextStepNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      debugLogger.debug(`OnboardingOperations: Moving to next step ${nextStepNumber}`);
    } else {
      debugLogger.debug('OnboardingOperations: Already at last step');
    }
  }, [currentStep, totalSteps, saveCurrentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      // Check if we're leaving the photos step (step 7)
      if (currentStep === 7) {
        debugLogger.debug('OnboardingOperations: Leaving photos step, executing cleanup');
        
        // Execute photo upload cleanup if available
        if (photoUploadCleanupRef.current) {
          try {
            photoUploadCleanupRef.current();
            debugLogger.debug('OnboardingOperations: Photo upload cleanup executed successfully');
          } catch (error) {
            debugLogger.error('OnboardingOperations: Error during photo upload cleanup:', error);
          }
        } else {
          debugLogger.warn('OnboardingOperations: No photo upload cleanup function registered');
        }

        // Clear any photo upload related localStorage
        try {
          localStorage.removeItem('photoUploadMetadata');
          debugLogger.debug('OnboardingOperations: Photo upload localStorage cleared');
        } catch (error) {
          debugLogger.error('OnboardingOperations: Error clearing localStorage:', error);
        }
      }

      const prevStepNumber = currentStep - 1;
      isManualNavigation.current = true;
      setCurrentStep(prevStepNumber);
      saveCurrentStep(prevStepNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      debugLogger.debug(`OnboardingOperations: Moving to previous step ${prevStepNumber}`);
    } else {
      debugLogger.debug('OnboardingOperations: Already at first step');
    }
  }, [currentStep, saveCurrentStep]);

  const checkUsernameAvailability = useCallback(async (username: string): Promise<boolean> => {
    try {
      if (!user) {
        debugLogger.error('checkUsernameAvailability: No user found');
        return false;
      }

      const normalizedUsername = username.toLowerCase().trim();
      debugLogger.debug('checkUsernameAvailability: Checking via RPC:', normalizedUsername, 'for user:', user.id);

      // Usar RPC com SECURITY DEFINER que bypassa RLS
      const { data: isAvailable, error } = await supabase
        .rpc('check_username_availability', {
          p_username: normalizedUsername,
          p_current_user_id: user.id
        });

      if (error) {
        debugLogger.error('checkUsernameAvailability: RPC error:', error);
        throw error;
      }

      debugLogger.debug('checkUsernameAvailability: Result:', isAvailable);
      return isAvailable === true;
    } catch (error) {
      debugLogger.error('checkUsernameAvailability: Exception:', error);
      throw error;
    }
  }, [user]);

  const saveOnboardingData = useCallback(async () => {
    if (!user) {
      debugLogger.error('saveOnboardingData: No user found');
      throw new Error('Usuário não encontrado');
    }

    setLoading(true);
    
    try {
      debugLogger.debug('saveOnboardingData: Starting final save with data:', data);

      // Validate required fields
      if (!data.username || data.username.trim() === '') {
        toast.error('Nome de usuário é obrigatório');
        throw new Error('Nome de usuário é obrigatório');
      }

      if (!data.birthdate) {
        toast.error('Data de nascimento é obrigatória');
        throw new Error('Data de nascimento é obrigatória');
      }

      // Calculate age for validation only, don't save it
      const calculatedAge = calculateAge(data.birthdate);
      if (calculatedAge < 18) {
        toast.error('Você precisa ter pelo menos 18 anos');
        throw new Error('Você precisa ter pelo menos 18 anos');
      }

      // Final validation - remaining required fields
      if (!data.gender || !data.orientation || !data.role) {
        throw new Error('Dados obrigatórios em falta');
      }

      // Prepare final data for save - REMOVIDO sponsorship_value
      const finalData = {
        username: data.username,
        gender: data.gender,
        orientation: data.orientation,
        role: data.role,
        relationship_type: data.relationshipType,
        birth_date: data.birthdate,
        // Campos opcionais (bio, education, occupation) serão preenchidos depois no perfil
        bio: data.bio || null,
        education: data.education || null,
        occupation: data.occupation || null,
        interests: data.interests || [],
        city: data.location?.city,
        state_code: data.location?.state ? data.location.state.substring(0, 2).toUpperCase() : null,
        latitude: data.geolocation?.latitude,
        longitude: data.geolocation?.longitude,
        // Preencher campos de localização ao completar onboarding
        last_location_update: data.geolocation?.latitude && data.geolocation?.longitude 
          ? new Date().toISOString() 
          : null,
        location_skip_count: 0,
        last_location_skip: null,
        share_income: data.shareIncome,
        // Características pessoais opcionais (serão preenchidas depois no perfil)
        height: data.height || null,
        body_type: data.bodyType || null,
        hair_color: data.hairColor || null,
        eye_color: data.eyeColor || null,
        smoker: data.smoker || null,
        drinker: data.drinker || null,
        piercings: data.piercings || null,
        tattoos: data.tattoos || null,
        travel_availability: data.travelAvailability || null,
        marital_status: data.maritalStatus || null,
        sigilo_presenca: data.sigilo_presenca || null,
        children: data.children || null,
        profile_photos: data.photos || [],
        main_photo_url: data.photos && data.photos.length > 0 ? data.photos[0] : null,
        photo_version: 'v1', // 🎯 FASE 3: Double guarantee all new profiles use v1
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
        last_onboarding_step: 7, // ATUALIZADO para 7
        updated_at: new Date().toISOString()
      };

      debugLogger.debug('saveOnboardingData: Final data to save:', finalData);

      const { error } = await supabase
        .from('profiles')
        .update(finalData)
        .eq('id', user.id);

      if (error) {
        debugLogger.error('saveOnboardingData: Database error:', error);
        throw error;
      }

      debugLogger.debug('saveOnboardingData: Onboarding completed successfully');
      
      // Update Datadog RUM with correct username
      try {
        datadogRum.setUser({
          id: user.id,
          email: user.email || '',
          name: data.username
        });
        debugLogger.debug('saveOnboardingData: Datadog RUM updated with new username:', data.username);
      } catch (rumError) {
        debugLogger.warn('saveOnboardingData: Failed to update Datadog RUM, but continuing:', rumError);
      }
      
      // Forçar reload de AMBOS os contextos ANTES de navegar para garantir sincronização total
      debugLogger.debug('saveOnboardingData: Refreshing profile contexts...');
      await refetchProfile();              // Atualiza ProfileContext
      await refreshProfileData();           // Atualiza useAuth.profileData
      debugLogger.debug('saveOnboardingData: Both contexts refreshed successfully');
      
      toast.success('Perfil criado com sucesso!');
      
      // Navigate to feed instead of main
      navigate('/main/feed');
    } catch (error: any) {
      debugLogger.error('saveOnboardingData: Exception:', error);
      const errorMessage = error?.message || 'Erro ao finalizar onboarding';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [data, user, setLoading, navigate, refetchProfile]);

  return {
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    prevStep,
    checkUsernameAvailability,
    saveOnboardingData,
    setCurrentStep,
    registerPhotoUploadCleanup
  };
};
